import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { signInAnonymously } from 'firebase/auth'
import { Alert, Box, Button, Card, Divider, useTheme, Typography } from '@mui/material'
import { auth } from 'providers/firebaseAuth'
import { useNavigate, useSearchParams } from 'react-router-dom-v5-compat'
import { get } from 'utils/lodash'
import { setSelectedLoginDetails } from 'slices/login/LoginPersistSlice'
import routes from 'router/routes'
import Loader from 'components/common/loader'
import { useRegisterAnonymousUserMutation } from 'api/account/registerUser'
import Constants from '../../Constants'
import { useLazyGetBusinessInfoQuery } from '../../api/business/getBusinessInfo'

const { PORTAL_AUTH_TENANT_ID } = Constants

const AnonymousLoginForm = ({ onBack }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [getBusinessInfo, { isFetching: isLoadingGetBusinessInfo, data: businessInfo, isSuccess: isGetBusinessInfoSuccess }] =
    useLazyGetBusinessInfoQuery()
  const [registerUser] = useRegisterAnonymousUserMutation()

  const businessName = searchParams.get('business_name')
  const accountId = searchParams.get('account_id')
  const invoiceId = searchParams.get('invoice_id')
  const statementId = searchParams.get('statement_id')

  const urlBusinessName = searchParams.get('business_name') || searchParams.get('bid')
  const oldBusinessName = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessName', ''), shallowEqual)
  const currentBusinessName = urlBusinessName || oldBusinessName
  const [storedBusinessId, setStoredBusinessId] = useState('')

  const goToHomePage = () => {
    if (invoiceId) {
      navigate(routes.app.payments)
    } else if (statementId) {
      navigate(routes.app.statements)
    } else {
      navigate(routes.app.home)
    }
  }

  useEffect(() => {
    if (currentBusinessName) {
      getBusinessInfo(currentBusinessName)
    }
  }, [businessInfo, currentBusinessName, getBusinessInfo])

  useEffect(() => {
    if (businessInfo?.id) {
      setStoredBusinessId(businessInfo.id)
    }
  }, [businessInfo])

  const handleAnonymousLogin = async () => {
    try {
      setIsLoading(true)
      setError('')

      auth.tenantId = PORTAL_AUTH_TENANT_ID
      console.log('Using tenant ID:', PORTAL_AUTH_TENANT_ID)

      const result = await signInAnonymously(auth)
      console.log('Anonymous sign in successful:', result)

      const { user } = result
      const userIdpId = user.uid
      const payload = {
        providerId: 'anonymous',
        userIdpId,
        businessId: storedBusinessId,
        accountId,
        email: null, // Anonymous users don't have emails
        firstName: 'Anonymous',
        lastName: 'User',
        isAnonymous: true,
      }

      try {
        const registerResponse = await registerUser(payload).unwrap()

        await auth.currentUser.getIdToken(true)

        dispatch(
          setSelectedLoginDetails({
            accountId,
            businessName: urlBusinessName,
            businessId: storedBusinessId,
            isAnonymous: true,
          })
        )

        goToHomePage()
      } catch (registerError) {
        console.error('Failed to register anonymous user:', registerError)
        setError('Failed to complete anonymous login. Please try again.')
      }
    } catch (err) {
      console.error('Anonymous sign-in error:', err)
      setError(`Error: ${err.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {isLoading && <Loader />}
      <Card sx={{ m: 'auto', width: '352px', boxShadow: `0px 1px 8px 0px ${theme.palette.divider}` }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }} align="center">
            One-Time Access
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }} align="center">
            Continue as a guest to view your account information
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Button variant="contained" color="primary" fullWidth onClick={handleAnonymousLogin} disabled={isLoading} sx={{ mb: 2 }}>
            Continue as Guest
          </Button>

          <Button
            variant="text"
            onClick={onBack}
            fullWidth
            sx={{
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            Go Back
          </Button>
        </Box>
      </Card>
    </>
  )
}

export default AnonymousLoginForm
